// centerlines.js — one line per wall instead of one per room face.
//
// Rooms trace the FACES of their walls, so a wall between two rooms comes out twice (once from each side).
// For a takeoff that's wrong for everything that's one per wall — studs, track, insulation — and it makes
// every shared wall two lines to click. Here every room edge (and every open-area face) is projected onto
// its wall's centreline, the stretches are unioned per wall, and the result is chained into polylines:
// one line per wall type, running through corners, windows and doors.
//
//   centerlineChains({ edges, runs, ftPerUnit, doorMode, doorMaxOpeningFt })
//     edges: room edges after assignTags ({a, b, kind: wall|window|door|opening, run, tag, lf}) — from ALL
//            rooms on the sheet, plus open-area faces ({a, b, kind:"wall", run, tag})
//     runs:  engine runs ({ux, uy, nx, ny, c, th, t0, t1}) sheet units
//   → [{ tag, pts, lf, run, runLf, windows:[lf], windowSegs:[[a,b]], doors:[lf], doorSegs:[[a,b]], loose }]
//     (loose: a face line kept as drawn because it couldn't be tied to a wall)
//
// Doors: the line crosses them (board runs over the header) and the door is deducted like a window —
// width × door height. doorMode "gap" leaves them out instead. A door is the standard symbol: a leaf hinged at a
// jamb with a solid swing arc (swings: engine doors [{hx,hy,cx,cy,r}]). An opening with no swing hinged at it is
// not a door — it's handed back in `doubtful` for the checks (a sliding / bifold door drawn without an arc). A pair
// is two leaves hinged at opposite jambs closing on each other; two singles side by side count as two doors.

const EPS = 1e-6;

export const DOUBLE_DOOR_FT = 4.75; // a door opening this wide or wider is a pair (typically 6'-0" × 7'-0")

// doubtful: optional array — door-sized gaps left out as "not a door" are pushed here ({a, b, lf}) so the caller can
// have them checked (AI / estimator) instead of losing a door whose swing the engine didn't read
export function centerlineChains({
  edges,
  runs,
  ftPerUnit,
  doorMode = "deduct",
  doorMaxOpeningFt = 4.5,
  swings = null,
  doubtful = null,
}) {
  const U = (inches) => inches / 12 / ftPerUnit;
  const segDist0 = (p, a, b) => {
    const dx = b[0] - a[0],
      dy = b[1] - a[1],
      L2 = dx * dx + dy * dy || 1e-12;
    const t = Math.max(
      0,
      Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2),
    );
    return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
  };
  // a swing spans this opening: its hinge AND the end of its leaf (closed, or as drawn) both sit on it
  const spans = (d, e) => {
    if (segDist0([d.hx, d.hy], e.a, e.b) > U(12)) return false;
    const ends = [[d.cx, d.cy]];
    const l = d.leafLine;
    if (l)
      ends.push(
        Math.hypot(l.x1 - d.hx, l.y1 - d.hy) >
          Math.hypot(l.x2 - d.hx, l.y2 - d.hy)
          ? [l.x1, l.y1]
          : [l.x2, l.y2],
      );
    return ends.some((q) => segDist0(q, e.a, e.b) <= U(12));
  };
  const swingAt = (e) => (swings || []).some((d) => spans(d, e));
  // single or pair: a pair has two swings hinged at OPPOSITE jambs (two leaves meeting in the middle); one swing is
  // a single door whatever the opening's width. With no swings to go on, a wide opening is taken as a pair.
  const doorKindOf = (o) => {
    const [a, b] = o.seg;
    const w = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (swings && swings.length && w > 0) {
      const ux = (b[0] - a[0]) / w,
        uy = (b[1] - a[1]) / w;
      const onLine = (x, y) => [
        (x - a[0]) * ux + (y - a[1]) * uy,
        Math.abs(-(x - a[0]) * uy + (y - a[1]) * ux),
      ];
      // the swings hung in this opening: hinge and closed-leaf end both on it
      const hung = swings.filter((d) => {
        if (d.r < w * 0.3 || d.r > w * 1.3) return false;
        const [th, oh] = onLine(d.hx, d.hy);
        if (!(oh <= U(12) && th >= -U(12) && th <= w + U(12))) return false;
        // the closed leaf lies along the opening: the swing's closed end, or the leaf line itself when it is
        // drawn closed
        const l = d.leafLine;
        const tips = [[d.cx, d.cy]];
        if (l)
          tips.push(
            Math.hypot(l.x1 - d.hx, l.y1 - d.hy) >
              Math.hypot(l.x2 - d.hx, l.y2 - d.hy)
              ? [l.x1, l.y1]
              : [l.x2, l.y2],
          );
        return tips.some(([x, y]) => {
          const [tc, oc] = onLine(x, y);
          return oc <= U(12) && tc >= -U(6) && tc <= w + U(6);
        });
      });
      // one leaf per hinge (the same swing can be found twice — from its leaf line and from its arc)
      const leaves = [];
      for (const d of hung)
        if (!leaves.some((x) => Math.hypot(x.hx - d.hx, x.hy - d.hy) < U(6)))
          leaves.push(d);
      if (leaves.length >= 2) {
        // two leaves hinged at opposite jambs: a pair closes on itself in the middle; two singles close apart
        const sorted = leaves
          .map((d) => [onLine(d.hx, d.hy)[0], d])
          .sort((x, y) => x[0] - y[0]);
        const [t1, p1] = sorted[0],
          [t2, p2] = sorted[sorted.length - 1];
        if (t2 - t1 >= w * 0.6) {
          const gap = Math.hypot(p1.cx - p2.cx, p1.cy - p2.cy);
          return gap > U(2.5) ? "two" : "double";
        }
        return "single";
      }
      if (leaves.length === 1) return "single";
    }
    return o.lf >= DOUBLE_DOOR_FT ? "double" : "single";
  };
  // door openings as counted: a stretch holding two single doors side by side becomes two doors
  const splitDoors = (drs) => {
    const out = [];
    for (const o of drs) {
      const k = doorKindOf(o);
      if (k !== "two") {
        out.push({ ...o, dk: k });
        continue;
      }
      const [a, b] = o.seg,
        m = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      out.push(
        { ...o, lf: o.lf / 2, seg: [a, m], dk: "single" },
        { ...o, lf: o.lf / 2, seg: [m, b], dk: "single" },
      );
    }
    return out;
  };
  // swings already accounted for: they span some door / opening of the sheet
  const usedSwing = new Set();
  if (swings)
    for (const e of edges || [])
      if (e.kind === "door" || e.kind === "opening")
        for (const d of swings) if (spans(d, e)) usedSwing.add(d);
  // a door-sized gap with no swing of its own, beside a swing that is another opening's door → not a door
  const besideOtherDoor = (e) => {
    const nearBy = swings.filter(
      (d) => segDist0([d.hx, d.hy], e.a, e.b) <= U(60),
    );
    return nearBy.length > 0 && nearBy.every((d) => usedSwing.has(d));
  };
  const byRun = new Map(); // run → [{t0, t1, kind, tag}]
  const loose = [];

  // which run a run-less edge lies on (bridged doors and openings carry no run): a parallel run whose
  // centreline is within half its thickness (+ slack) and whose extent reaches the edge
  const findRun = (e) => {
    const L = Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1]);
    if (L < EPS) return -1;
    const dx = (e.b[0] - e.a[0]) / L,
      dy = (e.b[1] - e.a[1]) / L;
    const mx = (e.a[0] + e.b[0]) / 2,
      my = (e.a[1] + e.b[1]) / 2;
    let best = -1,
      bd = Infinity;
    runs.forEach((R, i) => {
      if (!R || Math.abs(dx * R.ux + dy * R.uy) < 0.995) return;
      const d = Math.abs(R.nx * mx + R.ny * my - R.c);
      if (d > (R.th || 0) / 2 + U(3)) return;
      const ta = R.ux * e.a[0] + R.uy * e.a[1],
        tb = R.ux * e.b[0] + R.uy * e.b[1];
      const gap = Math.max(0, Math.min(ta, tb) - R.t1, R.t0 - Math.max(ta, tb));
      if (gap > U(12 * 9)) return;
      if (d + gap < bd) {
        bd = d + gap;
        best = i;
      }
    });
    return best;
  };

  // a run-less edge (a bridged door / opening) sits on the same wall as the ring edges just before / after it:
  // take their run when it's parallel and the edge lies in its band — so a door stays in its own wall's line
  const neighbourRun = (idx) => {
    const e = edges[idx];
    const L = Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1]);
    if (L < EPS) return -1;
    const dx = (e.b[0] - e.a[0]) / L,
      dy = (e.b[1] - e.a[1]) / L;
    const mx = (e.a[0] + e.b[0]) / 2,
      my = (e.a[1] + e.b[1]) / 2;
    for (let k = 1; k <= 3; k++)
      for (const d of [-1, 1]) {
        const o = edges[idx + d * k];
        if (!o || !(o.run >= 0) || !runs[o.run]) continue;
        const R = runs[o.run];
        if (Math.abs(dx * R.ux + dy * R.uy) < 0.995) continue;
        if (Math.abs(R.nx * mx + R.ny * my - R.c) > (R.th || 0) / 2 + U(6))
          continue;
        return o.run;
      }
    return -1;
  };
  for (let ei = 0; ei < (edges || []).length; ei++) {
    const e = edges[ei];
    let kind = e.kind || "wall",
      noSwing = false;
    const L = Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1]);
    if (L < EPS) continue;
    if (kind === "opening") {
      // door-sized gap with no swing drawn: treat as a door; wider: open plan → leave out
      if (L * ftPerUnit > doorMaxOpeningFt) continue;
      if (swings && !swingAt(e)) {
        // no swing hinged at it: a cased opening, the open end of a corridor, a gap under a tag — not a door.
        // (a sliding / pocket door drawn without a swing is handed to the checks — AI / estimator — instead)
        doubtful?.push({ a: e.a, b: e.b, lf: L * ftPerUnit });
        continue;
      }
      kind = "door";
    }
    // a "door" the room tracer bridged next to a swing that doesn't span it (the open end of a corridor beside a door)
    if (
      kind === "door" &&
      swings &&
      swings.length &&
      !swingAt(e) &&
      L * ftPerUnit <= doorMaxOpeningFt &&
      besideOtherDoor(e)
    ) {
      doubtful?.push({ a: e.a, b: e.b, lf: L * ftPerUnit });
      continue;
    }
    if (kind === "door" && doorMode === "gap") continue;
    let run = e.run >= 0 && runs[e.run] ? e.run : neighbourRun(ei);
    if (run < 0) run = findRun(e);
    if (run < 0) {
      if (kind === "wall" || kind === "window") loose.push({ ...e, kind });
      continue;
    }
    const R = runs[run];
    const ta = R.ux * e.a[0] + R.uy * e.a[1],
      tb = R.ux * e.b[0] + R.uy * e.b[1];
    if (!byRun.has(run)) byRun.set(run, []);
    byRun.get(run).push({
      t0: Math.min(ta, tb),
      t1: Math.max(ta, tb),
      kind,
      tag: kind === "door" ? null : e.tag || null,
      wet: !!e.wet,
      noSwing,
    });
  }

  // ── per wall: union of what the rooms on either side drew, as elementary pieces
  const segs = []; // { run, tag, t0, t1, ops:[{kind,t0,t1}] }
  const join = U(2.5); // pieces closer than this are one stretch (face corners, rounding)
  for (const [run, list] of byRun) {
    const R = runs[run];
    const cuts = [...new Set(list.flatMap((s) => [s.t0, s.t1]))].sort(
      (p, q) => p - q,
    );
    const pieces = [];
    for (let i = 0; i + 1 < cuts.length; i++) {
      const a = cuts[i],
        b = cuts[i + 1];
      if (b - a < EPS) continue;
      const cov = list.filter((s) => s.t0 <= a + EPS && s.t1 >= b - EPS);
      if (!cov.length) {
        pieces.push(null);
        continue;
      }
      // a door or window seen from either side is one
      const kind = cov.some((s) => s.kind === "door")
        ? "door"
        : cov.some((s) => s.kind === "window")
          ? "window"
          : "wall";
      const tags = new Map();
      for (const s of cov)
        if (s.tag) tags.set(s.tag, (tags.get(s.tag) || 0) + 1);
      const tag =
        [...tags.entries()].sort((p, q) => q[1] - p[1])[0]?.[0] || null;
      // a wall with a washroom (wet room) on either side is a washroom wall
      pieces.push({
        t0: a,
        t1: b,
        kind,
        tag,
        wet: cov.some((s) => s.wet),
        noSwing:
          kind === "door" &&
          cov.filter((s) => s.kind === "door").every((s) => s.noSwing),
      });
    }
    // doors carry no tag: they take the wall's tag on either side
    const real = pieces.filter(Boolean);
    for (let i = 0; i < real.length; i++) {
      if (real[i].tag) continue;
      const prev = real
          .slice(0, i)
          .reverse()
          .find((p) => p.tag),
        next = real.slice(i + 1).find((p) => p.tag);
      real[i].tag =
        (prev && (!next || real[i].t0 - prev.t1 <= next.t0 - real[i].t1)
          ? prev.tag
          : next?.tag) || null;
    }
    // stretches: consecutive pieces (gaps < join) with the same tag; a stretch that is only a door is dropped
    let cur = null;
    const flush = () => {
      if (cur && cur.ops.some((o) => o.kind === "wall" || o.kind === "window"))
        segs.push(cur);
      cur = null;
    };
    for (const p of real) {
      if (
        cur &&
        (p.t0 - cur.t1 > join ||
          p.tag !== cur.tag ||
          (p.kind !== "door" && p.wet !== cur.wet))
      )
        flush();
      if (!cur)
        cur = {
          run,
          tag: p.tag,
          wet: p.wet,
          t0: p.t0,
          t1: p.t1,
          ops: [],
          th: R.th || 0,
        };
      cur.t1 = Math.max(cur.t1, p.t1);
      const last = cur.ops[cur.ops.length - 1];
      if (last && last.kind === p.kind && p.t0 - last.t1 <= join) {
        last.t1 = p.t1;
        last.noSwing = last.noSwing && p.noSwing;
      } else
        cur.ops.push({
          kind: p.kind,
          t0: p.t0,
          t1: p.t1,
          noSwing: !!p.noSwing,
        });
    }
    flush();
  }

  // ── geometry: centreline points; a stretch end that stops at a crossing wall runs on to that wall's
  //    centreline (corners and tees meet in one point instead of stopping at the face)
  const P = (R, t) => [R.ux * t + R.nx * R.c, R.uy * t + R.ny * R.c];
  for (const s of segs) {
    const R = runs[s.run];
    s.A = P(R, s.t0);
    s.B = P(R, s.t1);
  }
  const lineX = (R1, R2) => {
    // intersection of two centrelines → t along R1
    const den = R1.ux * R2.uy - R1.uy * R2.ux;
    if (Math.abs(den) < 0.08) return null;
    const p1 = [R1.nx * R1.c, R1.ny * R1.c],
      p2 = [R2.nx * R2.c, R2.ny * R2.c];
    const t = ((p2[0] - p1[0]) * R2.uy - (p2[1] - p1[1]) * R2.ux) / den;
    return t;
  };
  for (const s of segs) {
    const R = runs[s.run];
    for (const end of ["t0", "t1"]) {
      let best = null;
      for (const o of segs) {
        if (o === s || o.run === s.run) continue;
        const R2 = runs[o.run];
        const t = lineX(R, R2);
        if (t == null) continue;
        const reach = (o.th || 0) / 2 + (s.th || 0) / 2 + U(4);
        if (Math.abs(t - s[end]) > reach) continue;
        // the crossing point must be on (or just past) the other wall
        const X = P(R, t),
          t2 = R2.ux * X[0] + R2.uy * X[1];
        if (t2 < o.t0 - reach || t2 > o.t1 + reach) continue;
        if (!best || Math.abs(t - s[end]) < Math.abs(best - s[end])) best = t;
      }
      if (best != null) {
        if (end === "t0") {
          s.t0 = Math.min(best, s.t1 - EPS);
          s.A = P(R, s.t0);
          if (s.ops[0]) s.ops[0].t0 = Math.min(s.ops[0].t0, s.t0);
        } else {
          s.t1 = Math.max(best, s.t0 + EPS);
          s.B = P(R, s.t1);
          const lo = s.ops[s.ops.length - 1];
          if (lo) lo.t1 = Math.max(lo.t1, s.t1);
        }
      }
    }
  }

  // ── chain stretches of one tag that meet end to end (a node where exactly two of that tag's ends meet)
  const tol = U(1.5);
  const near = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1]) <= tol * 1.5;
  const used = new Set();
  const ends = new Map(); // tag → [{s, end, p}]
  for (const s of segs)
    for (const [end, p] of [
      ["A", s.A],
      ["B", s.B],
    ]) {
      const k = `${s.tag || ""}|${s.wet ? 1 : 0}`; // one line = one type, washroom or not
      if (!ends.has(k)) ends.set(k, []);
      ends.get(k).push({ s, end, p });
    }
  const partners = (s, end) => {
    const p = s[end];
    const list = (ends.get(`${s.tag || ""}|${s.wet ? 1 : 0}`) || []).filter(
      (x) => x.s !== s && near(x.p, p),
    );
    return list.length === 1 ? list[0] : null; // exactly one other end there (a tee or cross breaks the line)
  };
  const opsOf = (s, forward) => {
    const R = runs[s.run];
    return s.ops
      .filter((o) => o.kind !== "wall")
      .map((o) => {
        const a = P(R, Math.max(o.t0, s.t0)),
          b = P(R, Math.min(o.t1, s.t1));
        return {
          kind: o.kind,
          lf:
            Math.max(0, Math.min(o.t1, s.t1) - Math.max(o.t0, s.t0)) *
            ftPerUnit,
          seg: forward ? [a, b] : [b, a],
          noSwing: !!o.noSwing,
        };
      })
      .filter((o) => o.lf >= 0.5);
  };
  const chains = [];
  for (const s0 of segs) {
    if (used.has(s0)) continue;
    // walk back to the start of this chain
    let start = s0,
      startEnd = "A";
    for (let g = 0; g < segs.length; g++) {
      const pr = partners(start, startEnd);
      if (!pr || used.has(pr.s) || pr.s === s0) break; // free end, or once round a closed loop
      start = pr.s;
      startEnd = pr.end === "A" ? "B" : "A";
    }
    // walk forward collecting pts
    const pts = [],
      ops = [],
      runLf = {};
    let s = start,
      inEnd = startEnd;
    let lf = 0;
    while (s && !used.has(s)) {
      used.add(s);
      const forward = inEnd === "A";
      const a = forward ? s.A : s.B,
        b = forward ? s.B : s.A;
      if (!pts.length) pts.push(a);
      else if (!near(pts[pts.length - 1], a)) pts.push(a);
      else pts[pts.length - 1] = a;
      pts.push(b);
      const len = (s.t1 - s.t0) * ftPerUnit;
      lf += len;
      runLf[s.run] = (runLf[s.run] || 0) + len;
      ops.push(...opsOf(s, forward));
      const nx = partners(s, forward ? "B" : "A");
      if (!nx || used.has(nx.s)) break;
      s = nx.s;
      inEnd = nx.end;
    }
    // close a loop exactly
    if (pts.length > 3 && near(pts[0], pts[pts.length - 1]))
      pts[pts.length - 1] = pts[0];
    let run = -1,
      bl = 0;
    for (const [k, v] of Object.entries(runLf))
      if (v > bl) {
        bl = v;
        run = Number(k);
      }
    const win = ops.filter((o) => o.kind === "window"),
      drs = splitDoors(ops.filter((o) => o.kind === "door"));
    const th = Math.max(0, ...Object.keys(runLf).map((k) => runs[k]?.th || 0));
    chains.push({
      tag: s0.tag,
      wet: !!s0.wet,
      pts: simplify(pts),
      lf,
      run,
      runLf,
      th,
      windows: win.map((o) => o.lf),
      windowSegs: win.map((o) => o.seg),
      doors: drs.map((o) => o.lf),
      doorSegs: drs.map((o) => o.seg),
      doorKinds: drs.map((o) => o.dk),
      doorNoSwing: drs.map((o) => !!o.noSwing),
    }); // (a door-sized opening with no swing drawn: flagged to check)
  }

  // edges that couldn't be tied to a wall: kept as drawn (one line each, merged when they touch)
  for (const e of loose) {
    const lf = Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1]) * ftPerUnit;
    const last = chains[chains.length - 1];
    if (
      last?.loose &&
      last.tag === (e.tag || null) &&
      last.wet === !!e.wet &&
      near(last.pts[last.pts.length - 1], e.a)
    ) {
      last.pts.push(e.b);
      last.lf += lf;
      continue;
    }
    chains.push({
      tag: e.tag || null,
      wet: !!e.wet,
      pts: [e.a, e.b],
      lf,
      run: -1,
      runLf: {},
      windows: e.kind === "window" ? [lf] : [],
      windowSegs: e.kind === "window" ? [[e.a, e.b]] : [],
      doors: [],
      doorSegs: [],
      doorKinds: [],
      loose: true,
    });
  }
  return chains.filter((c) => c.lf >= 0.25);
}

// drop repeated and in-line points (a straight run is two points, however many pieces made it)
export function simplify(pts, eps = 1e-6) {
  const p = [];
  for (const q of pts)
    if (
      !p.length ||
      Math.hypot(q[0] - p[p.length - 1][0], q[1] - p[p.length - 1][1]) > eps
    )
      p.push(q);
  const closed =
    p.length > 3 &&
    Math.hypot(p[0][0] - p[p.length - 1][0], p[0][1] - p[p.length - 1][1]) <=
      eps;
  const out = [];
  for (let i = 0; i < p.length; i++) {
    if (i === 0 || i === p.length - 1) {
      out.push(p[i]);
      continue;
    }
    const a = out[out.length - 1],
      b = p[i],
      c = p[i + 1];
    const cr = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
    const L = Math.hypot(c[0] - a[0], c[1] - a[1]) || 1;
    const dot = (b[0] - a[0]) * (c[0] - b[0]) + (b[1] - a[1]) * (c[1] - b[1]);
    if (Math.abs(cr) / L <= Math.max(eps, L * 1e-4) && dot >= 0) continue; // straight on
    out.push(b);
  }
  if (closed && out.length > 3) {
    // the start point of a loop may sit mid-side
    const a = out[out.length - 2],
      b = out[0],
      c = out[1];
    const cr = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
    if (Math.abs(cr) / (Math.hypot(c[0] - a[0], c[1] - a[1]) || 1) <= 1e-4) {
      out.shift();
      out[out.length - 1] = out[0];
    }
  }
  return out;
}

// Outside corners (corner bead): every corner that sticks out into a room — a reflex corner of the room's
// outline (a pilaster, a bulkhead return, a wall that jogs, a core inside an open office) — plus both faces of a
// wall that ends at a wide opening (no door frame there). Door jambs are left out — the frame covers them, so
// no bead at a door: not the jambs, not the wall-face corners beside them, not the tip of a door leaf.
//   rooms: [{ ring, edges:[{a,b,kind}] }] sheet units → [[x,y]] (one per corner, deduplicated)
export function outsideCorners(
  rooms,
  {
    ftPerUnit,
    doorMaxOpeningFt = 4.5,
    // Corner bead is quick to add by hand, a wrong one is easy to miss — so only clear outside corners count:
    // both legs real wall (≥ 18"), a near-square turn, not at a door / opening / window, on a traced wall
    minLegIn = 18,
    minTurn = 0.85, // |sin| of the turn: ≥ ~58°
    wallEnds = false, // ends of walls at wide openings (open plan) — off: too many are not bead
    doors = [],
    doorSegs = [],
    lines = null,
  } = {},
) {
  const U = (inches) => inches / 12 / ftPerUnit;
  const out = [];
  // one corner per wall corner: the two faces' corners of one wall end / bend are a wall-thickness apart
  const add = (p) => {
    if (!out.some((q) => Math.hypot(q[0] - p[0], q[1] - p[1]) < U(18)))
      out.push(p);
  };
  // only corners of WALLS: a corner must sit on a traced wall line (within half a thick wall) — the
  // corners of furniture, vending machines, millwork or fixtures a room outline wraps around don't count
  const onWall = (p) => {
    if (!lines) return true;
    // (a face corner is half the wall's thickness off its centreline — a wall end's corner ~0.7 of it)
    for (const ln of lines) {
      const pts = ln.pts || ln,
        tol = (ln.th ? ln.th * 0.75 : U(6)) + U(1);
      for (let i = 0; i + 1 < pts.length; i++) {
        const a = pts[i],
          b = pts[i + 1],
          vx = b[0] - a[0],
          vy = b[1] - a[1],
          L2 = vx * vx + vy * vy;
        const k = L2
          ? Math.max(
              0,
              Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L2),
            )
          : 0;
        if (Math.hypot(a[0] + vx * k - p[0], a[1] + vy * k - p[1]) <= tol)
          return true;
      }
    }
    return false;
  };
  const near = (p, q, t) => Math.hypot(p[0] - q[0], p[1] - q[1]) <= t;
  for (const r of rooms || []) {
    const ring = r.ring || [];
    const n = ring.length;
    if (n < 4) continue;
    let A = 0;
    for (let i = 0; i < n; i++) {
      const p = ring[i],
        q = ring[(i + 1) % n];
      A += p[0] * q[1] - q[0] * p[1];
    }
    const sgn = Math.sign(A) || 1;
    // door / opening edges and keyhole seams on this outline
    const doorish = (r.edges || []).filter(
      (e) => e.kind === "door" || e.kind === "opening",
    );
    const isSeam = (a, b) => {
      for (let k = 0; k < n; k++) {
        const c = ring[k],
          d = ring[(k + 1) % n];
        if (near(c, b, 1e-6) && near(d, a, 1e-6)) return true;
      }
      return false;
    };
    for (let i = 0; i < n; i++) {
      const a = ring[(i - 1 + n) % n],
        v = ring[i],
        b = ring[(i + 1) % n];
      const l1 = Math.hypot(v[0] - a[0], v[1] - a[1]),
        l2 = Math.hypot(b[0] - v[0], b[1] - v[1]);
      // a true corner: both legs ≥ 18". A wall end standing free in a room: one long leg and a short cap
      // (the wall's thickness) whose other end turns the same way — one bead at that end
      const lo = Math.min(l1, l2),
        hi = Math.max(l1, l2);
      if (hi < U(minLegIn) || lo < U(2)) continue;
      if (lo < U(minLegIn)) {
        if (lo > U(12)) continue; // a short return / pier, not a wall end
        // walk round the short side: an END CAP turns the same way again within ~14" (a step / jog turns back)
        const turnAt = (k) => {
          const p = ring[(k - 1 + n) % n],
            q = ring[k],
            w = ring[(k + 1) % n];
          return Math.sign(
            (q[0] - p[0]) * (w[1] - q[1]) - (q[1] - p[1]) * (w[0] - q[0]),
          );
        };
        const dir = l1 < l2 ? -1 : 1;
        let k = i,
          walked = 0,
          cap = false;
        for (let st = 0; st < 5; st++) {
          const k2 = (k + dir + n) % n;
          walked += Math.hypot(
            ring[k2][0] - ring[k][0],
            ring[k2][1] - ring[k][1],
          );
          if (walked > U(14)) break;
          k = k2;
          const t = turnAt(k);
          // turns back: a jog. A tiny one (faces drawn a hair apart at the end) is walked past.
          if (t === sgn) {
            const nx = ring[(k + dir + n) % n];
            if (Math.hypot(nx[0] - ring[k][0], nx[1] - ring[k][1]) > U(4))
              break;
            continue;
          }
          if (t !== 0) {
            cap = true;
            break;
          }
        }
        if (!cap) continue;
      }
      const cr = (v[0] - a[0]) * (b[1] - v[1]) - (v[1] - a[1]) * (b[0] - v[0]);
      if (Math.abs(cr) / (l1 * l2) < minTurn) continue; // a shallow bend, not a corner
      if (Math.sign(cr) === sgn) continue; // turning the room's way: an inside corner
      if (isSeam(a, v) || isSeam(v, b)) continue;
      // both sides of the corner are wall (not a door, opening or window edge)
      const edgeAt = (p, q) =>
        (r.edges || []).find(
          (e) =>
            (near(e.a, p, U(2)) && near(e.b, q, U(2))) ||
            (near(e.a, q, U(2)) && near(e.b, p, U(2))),
        );
      const e1 = edgeAt(a, v),
        e2 = edgeAt(v, b);
      if ((e1 && e1.kind !== "wall") || (e2 && e2.kind !== "wall")) continue;
      // …and the wall runs on along both legs for 18" (not a short return, a pier or a window jamb)
      if (r.edges?.length) {
        const wallLen = (p, dir) => {
          let L = 0;
          for (const e of r.edges)
            if (e.kind === "wall" && (near(e.a, p, U(2)) || near(e.b, p, U(2))))
              L = Math.max(L, Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1]));
          return L;
        };
        if (wallLen(v) < U(Math.min(minLegIn, 12))) continue;
      }
      // windows: no bead at a window return
      if (
        (r.edges || []).some(
          (e) =>
            e.kind === "window" && (near(e.a, v, U(12)) || near(e.b, v, U(12))),
        )
      )
        continue;
      // a jamb: the corner sits on a door / opening edge (jambs are added below, once per side)
      if (doorish.some((e) => near(e.a, v, U(6)) || near(e.b, v, U(6))))
        continue;
      // the tip of an open door leaf (drawn as a thin box beside the wall) isn't a wall corner
      if (
        doors.some((d) => {
          const dh = Math.hypot(v[0] - d.hx, v[1] - d.hy);
          return (
            Math.abs(dh - d.r) < U(6) &&
            dh > d.r * 0.5 &&
            !near(v, [d.cx, d.cy], U(6))
          );
        })
      )
        continue;
      if (doorSegs.some(([p, q]) => near(p, v, U(8)) || near(q, v, U(8))))
        continue;
      if (!onWall(v)) continue;
      add(v);
    }
    // a wall that stops at a wide opening: its end has a corner on this room's side
    // (door openings are handled below from the wall lines: one corner at each jamb)
    for (const e of wallEnds ? doorish : []) {
      if (e.kind !== "opening") continue;
      if (
        Math.hypot(e.b[0] - e.a[0], e.b[1] - e.a[1]) * ftPerUnit <=
        doorMaxOpeningFt
      )
        continue;
      if (onWall(e.a)) add(e.a);
      if (onWall(e.b)) add(e.b);
    }
  }
  // doors: no corner bead at a door — drop any corner at (or within a wall thickness of) a jamb
  const jambs = doorSegs.flatMap(([p, q]) => [p, q]);
  return out.filter(
    (o) => !jambs.some((j) => Math.hypot(o[0] - j[0], o[1] - j[1]) < U(18)),
  );
}

// ── trace clean-up ────────────────────────────────────────────────────────────────────────────────────
const segDist = ([px, py], [ax, ay], [bx, by]) => {
  const dx = bx - ax,
    dy = by - ay,
    L2 = dx * dx + dy * dy || 1e-12;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / L2));
  return Math.hypot(px - ax - t * dx, py - ay - t * dy);
};
// does segment p→q pass through segment a→b (a→b counted `tol` longer at both ends — the tick at a dimension's end)
const crosses = (p, q, a, b, tol = 0) => {
  const rx = q[0] - p[0],
    ry = q[1] - p[1],
    sx = b[0] - a[0],
    sy = b[1] - a[1];
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-9) return false;
  const t = ((a[0] - p[0]) * sy - (a[1] - p[1]) * sx) / den; // along p→q
  const u = ((a[0] - p[0]) * ry - (a[1] - p[1]) * rx) / den; // along a→b
  const sl = Math.hypot(sx, sy) || 1;
  return t > 0.02 && t < 0.98 && u >= -tol / sl && u <= 1 + tol / sl;
};

/**
 * Open-area wall faces (walls no closed room drew) are where most false positives come from. Keep one only if
 *  • it joins the traced walls — an end within ~18" of a room wall or of another kept face (lines floating
 *    off the building — a neighbour's outline, a site line — are dropped), and
 *  • it isn't a piece of a dimension string — short faces crossing a dimension line (the double ticks between
 *    dimensions) or lying along one are dropped.
 * faces: [{a, b, ...}] · roomEdges: [{a, b}] · dims: [{a, b, h}] (scaleCheck.dimensionLines) — sheet units
 */
export function filterOpenFaces(
  faces,
  { roomEdges = [], dims = [], ftPerUnit },
) {
  const U = (inches) => inches / 12 / ftPerUnit;
  const len = (f) => Math.hypot(f.b[0] - f.a[0], f.b[1] - f.a[1]);
  const onDim = (f) =>
    dims.some((d) => {
      const tol = Math.max(d.h || 0, U(4));
      // short faces crossing a dimension line (ticks) or ending on one (extension lines)
      if (
        len(f) < U(60) &&
        (crosses(f.a, f.b, d.a, d.b, tol) ||
          segDist(f.a, d.a, d.b) <= tol ||
          segDist(f.b, d.a, d.b) <= tol)
      )
        return true;
      // along a dimension line: both ends close to it
      return segDist(f.a, d.a, d.b) <= tol && segDist(f.b, d.a, d.b) <= tol;
    });
  let pool = faces.filter((f) => !onDim(f));
  const anchors = roomEdges.map((e) => [e.a, e.b]);
  // 18": a face often stops a wall-thickness short of the room wall it meets (the junction belongs to the room)
  const near = (p, segs) => segs.some(([a, b]) => segDist(p, a, b) <= U(18));
  const kept = [];
  for (let changed = true; changed; ) {
    changed = false;
    const rest = [];
    for (const f of pool) {
      if (near(f.a, anchors) || near(f.b, anchors)) {
        kept.push(f);
        anchors.push([f.a, f.b]);
        changed = true;
      } else rest.push(f);
    }
    pool = rest;
  }
  return kept;
}

/**
 * One line per wall: a line lying inside a longer, parallel line's wall (a dashed line drawn inside a wall,
 * a window sill, the outer face of an exterior wall) is the same wall twice — drop it.
 * chains: centerlineChains() output (pts, th, lf, …) — sheet units.
 */
export function dropNestedLines(chains, { ftPerUnit }) {
  const U = (inches) => inches / 12 / ftPerUnit;
  const segsOf = (c) =>
    c.pts
      .slice(1)
      .map((p, i) => [c.pts[i], p])
      .filter(([a, b]) => Math.hypot(b[0] - a[0], b[1] - a[1]) > 1e-6);
  const S = chains.map(segsOf);
  const lenOf = (c, i) =>
    S[i].reduce((n, [a, b]) => n + Math.hypot(b[0] - a[0], b[1] - a[1]), 0);
  const L = chains.map(lenOf);
  const order = chains.map((_, i) => i).sort((i, j) => L[j] - L[i]);
  const drop = new Set();
  for (const i of order) {
    if (drop.has(i) || L[i] <= 0) continue;
    let covered = 0;
    for (const [a, b] of S[i]) {
      const sl = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const ux = (b[0] - a[0]) / sl,
        uy = (b[1] - a[1]) / sl;
      const iv = []; // covered stretches of this segment, in its own [0, sl]
      for (const j of order) {
        if (j === i || drop.has(j) || L[j] < L[i]) continue;
        if (L[j] === L[i] && j > i) continue;
        const band = ((chains[i].th || 0) + (chains[j].th || 0)) / 2 + U(1.5);
        if (band > U(16)) continue;
        for (const [p, q] of S[j]) {
          const tl = Math.hypot(q[0] - p[0], q[1] - p[1]);
          const vx = (q[0] - p[0]) / tl,
            vy = (q[1] - p[1]) / tl;
          if (Math.abs(ux * vy - uy * vx) > 0.05) continue; // not parallel (~3°)
          const off = Math.abs((a[0] - p[0]) * -vy + (a[1] - p[1]) * vx); // distance between the two lines
          if (off > Math.max(band, U(3))) continue;
          const s0 = (p[0] - a[0]) * ux + (p[1] - a[1]) * uy,
            s1 = (q[0] - a[0]) * ux + (q[1] - a[1]) * uy;
          const lo = Math.max(0, Math.min(s0, s1)),
            hi = Math.min(sl, Math.max(s0, s1));
          if (hi > lo) iv.push([lo, hi]);
        }
      }
      iv.sort((x, y) => x[0] - y[0]);
      let end = -Infinity;
      for (const [lo, hi] of iv) {
        if (hi <= end) continue;
        covered += hi - Math.max(lo, end);
        end = hi;
      }
    }
    if (covered >= 0.8 * L[i]) drop.add(i);
  }
  return chains.filter((_, i) => !drop.has(i));
}

/**
 * An area's outline with as few corners as it needs: corners within `tol` of the straight line through their
 * neighbours (small jogs at wall ends and pilasters, pixel steps) are dropped. Corners that appear twice — the
 * seam where an outline wraps around a hole — are always kept, so holes stay holes.
 * ring: [[x,y],…] (open or closed) · tol: same units → [[x,y],…] open ring
 */
export function simplifyRing(ring, tol) {
  let pts = ring.slice();
  if (
    pts.length > 1 &&
    Math.hypot(
      pts[0][0] - pts[pts.length - 1][0],
      pts[0][1] - pts[pts.length - 1][1],
    ) < 1e-9
  )
    pts.pop();
  if (pts.length <= 4 || !(tol > 0)) return pts;
  const key = (p) => `${Math.round(p[0] * 100)},${Math.round(p[1] * 100)}`;
  const cnt = new Map();
  for (const p of pts) cnt.set(key(p), (cnt.get(key(p)) || 0) + 1);
  const pinned = pts.map((p) => cnt.get(key(p)) > 1);
  const dist = (p, a, b) => {
    const dx = b[0] - a[0],
      dy = b[1] - a[1],
      L2 = dx * dx + dy * dy;
    if (!L2) return Math.hypot(p[0] - a[0], p[1] - a[1]);
    const t = Math.max(
      0,
      Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2),
    );
    return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
  };
  // Douglas–Peucker between anchors: pinned corners, plus the two corners farthest apart (a closed ring needs two)
  let i0 = 0,
    i1 = 0,
    bd = -1;
  for (let i = 0; i < pts.length; i++) {
    const d = Math.hypot(pts[i][0] - pts[0][0], pts[i][1] - pts[0][1]);
    if (d > bd) {
      bd = d;
      i1 = i;
    }
  }
  const anchors = [
    ...new Set([
      i0,
      i1,
      ...pinned.map((v, i) => (v ? i : -1)).filter((i) => i >= 0),
    ]),
  ].sort((a, b) => a - b);
  const keep = new Uint8Array(pts.length);
  for (const a of anchors) keep[a] = 1;
  const dp = (a, b) => {
    // indices along the ring (b may wrap past the end)
    const n = pts.length;
    let far = -1,
      fd = tol;
    for (let k = a + 1; k < b; k++) {
      const d = dist(pts[k % n], pts[a % n], pts[b % n]);
      if (d > fd) {
        fd = d;
        far = k;
      }
    }
    if (far < 0) return;
    keep[far % n] = 1;
    dp(a, far);
    dp(far, b);
  };
  for (let j = 0; j < anchors.length; j++) {
    const a = anchors[j],
      b = j + 1 < anchors.length ? anchors[j + 1] : anchors[0] + pts.length;
    dp(a, b);
  }
  const out = pts.filter((_, i) => keep[i]);
  return out.length >= 3 ? out : pts;
}

/**
 * The exterior wall as ONE continuous line. `bands` (wallEngine exteriorBands) are the outside walls measured across
 * their whole assembly; here they are joined round the corners (and across doors / openings in them) into polylines
 * through the middle of the assembly. Traced lines lying inside an exterior band — the several parallel lines a wide,
 * noisy masonry wall otherwise gives — are replaced by it; their windows and doors move onto it (so windows are still
 * deducted). Its type comes from the tags printed OUTSIDE the building against it (X.2, X.2a…), the nearest one along
 * the line; tags inside (furring, demising-wall tags at tees) are ignored.
 *   lines: traced lines (centerlineChains → retagLinesByEvidence) · tagCands: engine tag candidates (with tx, ty)
 *   → lines with the exterior ones replaced: [{…line, exterior: true}]
 */
export function applyExterior(
  lines,
  {
    bands = [],
    tagCands = [],
    runs = [],
    ftPerUnit,
    has = () => true,
    minFt = 20,
    isExterior = null,
  },
) {
  // isExterior(tag): the job's exterior assemblies (named EXTERIOR / VENEER / CLADDING …). When the job has them, an
  // outside wall is only taken as the exterior where one of them is tagged against it — a wall that merely faces a
  // space open to the sheet edge (a plan cut at a match line) is left as traced.
  if (!bands.length) return lines;
  const U = (inch) => inch / 12 / ftPerUnit;
  const P = (b, t) => [b.ux * t + b.nx * b.off, b.uy * t + b.ny * b.off];
  const dist = (p, q) => Math.hypot(p[0] - q[0], p[1] - q[1]);
  const inBand = (p, slack = U(2)) =>
    bands.find((b) => {
      const t = b.ux * p[0] + b.uy * p[1],
        o = b.nx * p[0] + b.ny * p[1];
      return (
        Math.abs(o - b.off) <= b.th / 2 + slack &&
        t >= b.t0 - U(6) &&
        t <= b.t1 + U(6)
      );
    }) || null;
  // the places along the exterior where its type may change (outer-layer piece ends, band ends)
  const cutPts = bands.flatMap((b) => [
    P(b, b.t0),
    P(b, b.t1),
    ...(b.cuts || []).map((t) => P(b, t)),
  ]);
  // ── segments, ends met at the corners and joined across gaps
  const segs = bands.map((b) => ({ b, t0: b.t0, t1: b.t1 }));
  const lineX = (b1, b2) => {
    const den = b1.ux * b2.uy - b1.uy * b2.ux;
    if (Math.abs(den) < 0.08) return null;
    const p1 = [b1.nx * b1.off, b1.ny * b1.off],
      p2 = [b2.nx * b2.off, b2.ny * b2.off];
    return ((p2[0] - p1[0]) * b2.uy - (p2[1] - p1[1]) * b2.ux) / den;
  };
  for (const s of segs)
    for (const end of ["t0", "t1"]) {
      let best = null;
      for (const o of segs) {
        if (o === s) continue;
        const t = lineX(s.b, o.b);
        if (t == null) continue;
        const reach = (s.b.th + o.b.th) / 2 + U(30);
        if (Math.abs(t - s[end]) > reach) continue;
        const X = P(s.b, t),
          t2 = o.b.ux * X[0] + o.b.uy * X[1];
        if (t2 < o.t0 - reach || t2 > o.t1 + reach) continue;
        if (best == null || Math.abs(t - s[end]) < Math.abs(best - s[end]))
          best = t;
      }
      if (best != null) s[end] = best;
    }
  // collinear pieces of one wall across a door / opening: one segment
  for (let i = 0; i < segs.length; i++)
    for (let j = i + 1; j < segs.length; j++) {
      const a = segs[i],
        c = segs[j];
      if (!a || !c) continue;
      if (Math.abs(a.b.ux * c.b.ux + a.b.uy * c.b.uy) < 0.999) continue;
      const sg = a.b.nx * c.b.nx + a.b.ny * c.b.ny > 0 ? 1 : -1;
      if (Math.abs(a.b.off - sg * c.b.off) > U(4)) continue;
      const sgT = a.b.ux * c.b.ux + a.b.uy * c.b.uy > 0 ? 1 : -1;
      const c0 = Math.min(sgT * c.t0, sgT * c.t1),
        c1 = Math.max(sgT * c.t0, sgT * c.t1);
      if (c0 > a.t1 + U(96) || c1 < a.t0 - U(96)) continue;
      a.t0 = Math.min(a.t0, c0);
      a.t1 = Math.max(a.t1, c1);
      a.b = { ...a.b, th: Math.max(a.b.th, c.b.th) };
      segs[j] = null;
    }
  const S = segs
    .filter(Boolean)
    .map((s) => ({ A: P(s.b, s.t0), B: P(s.b, s.t1), b: s.b }));
  // chain ends that meet
  const used = new Set(),
    chains = [];
  const tol = U(3);
  const other = (s, p) =>
    S.filter(
      (o) =>
        o !== s && !used.has(o) && (dist(o.A, p) <= tol || dist(o.B, p) <= tol),
    );
  for (const s0 of S) {
    if (used.has(s0)) continue;
    // walk back to a free end
    let start = s0,
      startPt = s0.A;
    const seen = new Set([s0]);
    for (;;) {
      const nb = S.filter(
        (o) =>
          o !== start &&
          !seen.has(o) &&
          (dist(o.A, startPt) <= tol || dist(o.B, startPt) <= tol),
      );
      if (nb.length !== 1) break;
      const o = nb[0];
      seen.add(o);
      startPt = dist(o.A, startPt) <= tol ? o.B : o.A;
      start = o;
    }
    const pts = [startPt];
    let cur = start,
      at = startPt;
    let th = 0;
    while (cur && !used.has(cur)) {
      used.add(cur);
      const far = dist(cur.A, at) <= dist(cur.B, at) ? cur.B : cur.A;
      pts.push(far);
      th = Math.max(th, cur.b.th);
      at = far;
      const nb = other(cur, at);
      cur = nb.length === 1 ? nb[0] : null;
    }
    if (pts.length > 3 && dist(pts[0], pts[pts.length - 1]) <= tol)
      pts[pts.length - 1] = pts[0];
    const len = pts.reduce((t, p, i) => (i ? t + dist(p, pts[i - 1]) : 0), 0);
    if (len * ftPerUnit >= minFt) chains.push({ pts: simplify(pts), th });
  }
  if (!chains.length) return lines;
  const onChain = (p) => {
    let best = null,
      bd = Infinity;
    chains.forEach((c, ci) => {
      if (!live[ci]) return;
      for (let i = 1; i < c.pts.length; i++) {
        const a = c.pts[i - 1],
          b = c.pts[i];
        const dx = b[0] - a[0],
          dy = b[1] - a[1],
          L2 = dx * dx + dy * dy || 1e-12;
        const k = Math.max(
          0,
          Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2),
        );
        const d = Math.hypot(p[0] - a[0] - k * dx, p[1] - a[1] - k * dy);
        if (d < bd) {
          bd = d;
          best = ci;
        }
      }
    });
    return best != null && bd <= chains[best].th / 2 + U(4) ? best : -1;
  };
  // where a point sits along a chain (distance from its start) and how far off it
  const sOfChain = (c, p) => {
    const pts = c.pts;
    let best = 0,
      bd = Infinity,
      acc = 0;
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1],
        b = pts[i];
      const dx = b[0] - a[0],
        dy = b[1] - a[1],
        L2 = dx * dx + dy * dy || 1e-12;
      const k = Math.max(
        0,
        Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2),
      );
      const d = Math.hypot(p[0] - a[0] - k * dx, p[1] - a[1] - k * dy);
      if (d < bd) {
        bd = d;
        best = acc + k * Math.sqrt(L2);
      }
      acc += Math.sqrt(L2);
    }
    return [best, bd];
  };
  // the tags printed OUTSIDE against each chain (the exterior's own type marks), along it
  const marksOf = chains.map((c) => {
    const marks = [];
    const seen = new Set();
    for (const t of tagCands) {
      if (!has(t.text)) continue;
      if (isExterior && !isExterior(t.text)) continue;
      const land = [t.x, t.y];
      const b = inBand(land, U(6));
      if (!b) continue;
      const [, dl] = sOfChain(c, land);
      if (dl > c.th / 2 + U(6)) continue;
      // the tag itself is outside the building (beyond the band's outer face)
      const ctr = [t.tx ?? t.x, t.ty ?? t.y];
      const o = b.nx * ctr[0] + b.ny * ctr[1] - b.off;
      if (Math.sign(o) !== b.side || Math.abs(o) < b.th / 2 - U(2)) continue;
      // one mark per printed tag, where the tag sits against the wall (its centre, squared onto the line) —
      // arrows from notes nearby ("BRICK PROJECTION – SEE DETAIL") don't move it
      const key = `${t.text}|${Math.round(ctr[0])}|${Math.round(ctr[1])}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const [sc, dc] = sOfChain(c, ctr);
      marks.push({
        s: dc <= c.th / 2 + U(48) ? sc : sOfChain(c, land)[0],
        text: t.text,
      });
    }
    return marks.sort((a, b) => a.s - b.s);
  });
  // with exterior assemblies on the job, a chain nobody tagged as exterior isn't the exterior
  const live = chains.map((c, ci) => !isExterior || marksOf[ci].length > 0);
  if (!live.some(Boolean)) return lines;
  // ── traced lines inside the bands go; their windows / doors come with them
  const kept = [];
  const moved = chains.map(() => ({
    windows: [],
    windowSegs: [],
    doors: [],
    doorSegs: [],
    doorKinds: [],
    doorNoSwing: [],
    tagLf: new Map(),
  }));
  for (const l of lines) {
    const pts = l.pts || [];
    let inLen = 0,
      tot = 0;
    for (let i = 1; i < pts.length; i++) {
      const L = dist(pts[i], pts[i - 1]);
      tot += L;
      const m = [
        (pts[i][0] + pts[i - 1][0]) / 2,
        (pts[i][1] + pts[i - 1][1]) / 2,
      ];
      if (inBand(m) && inBand(pts[i]) && inBand(pts[i - 1])) inLen += L;
    }
    if (!tot || inLen < tot * 0.7) {
      kept.push(l);
      continue;
    }
    const mid = pts[Math.floor(pts.length / 2)];
    const ci = onChain(mid);
    if (ci < 0) {
      kept.push(l);
      continue;
    }
    const m = moved[ci];
    (l.windowSegs || []).forEach((sg, k) => {
      m.windowSegs.push(sg);
      m.windows.push(l.windows?.[k] ?? dist(sg[0], sg[1]) * ftPerUnit);
    });
    (l.doorSegs || []).forEach((sg, k) => {
      m.doorSegs.push(sg);
      m.doors.push(l.doors?.[k] ?? dist(sg[0], sg[1]) * ftPerUnit);
      m.doorKinds.push(l.doorKinds?.[k] || "single");
      m.doorNoSwing.push(!!l.doorNoSwing?.[k]);
    });
    if (l.tag) m.tagLf.set(l.tag, (m.tagLf.get(l.tag) || 0) + (l.lf || 0));
  }
  // ── the exterior's type: tags printed outside against it, nearest along the line
  const out = [...kept];
  chains.forEach((c, ci) => {
    if (!live[ci]) return;
    const pts = c.pts;
    const cum = [0];
    for (let i = 1; i < pts.length; i++)
      cum.push(cum[i - 1] + dist(pts[i], pts[i - 1]));
    const total = cum[cum.length - 1];
    const sOf = (p) => sOfChain(c, p);
    const marks = marksOf[ci];
    const fallback =
      [...moved[ci].tagLf.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ||
      null;
    // where the wall changes along it: the outer layer's piece ends (a brick pier, a window unit, a projection),
    // the ends of the bands it was joined from, and its corners. Each stretch between them takes the exterior
    // tag landing on it; a stretch with none takes the nearest tagged stretch's type along the wall.
    const cutS = [0, total, ...cum.slice(1, -1)];
    for (const p of cutPts) {
      const [sp, dp] = sOf(p);
      if (dp <= c.th / 2 + U(4) && sp > U(3) && sp < total - U(3))
        cutS.push(sp);
    }
    cutS.sort((x, y) => x - y);
    const stops = cutS.filter((v, i) => !i || v - cutS[i - 1] > U(4));
    // one stretch per run of the same tag along the wall; between two different tags the change goes at the
    // physical break between them nearest their midpoint (a pier end, a window jamb, a corner), else the midpoint
    const pieces = [];
    if (!marks.length)
      pieces.push({ s0: 0, s1: total, tag: fallback, far: true });
    else {
      let s0 = 0,
        tag = marks[0].text;
      for (let i = 1; i < marks.length; i++) {
        if (marks[i].text === tag) continue;
        const lo = marks[i - 1].s,
          hi = marks[i].s,
          midS = (lo + hi) / 2;
        let cut = midS,
          bd = Infinity;
        for (const st of stops)
          if (st > lo + U(2) && st < hi - U(2) && Math.abs(st - midS) < bd) {
            bd = Math.abs(st - midS);
            cut = st;
          }
        pieces.push({ s0, s1: cut, tag, far: false });
        s0 = cut;
        tag = marks[i].text;
      }
      pieces.push({ s0, s1: total, tag, far: false });
      // the ends beyond the first / last tag are carried — dashed when that's a long way
      if (marks[0].s > U(15 * 12))
        pieces[0].far = pieces.length === 1 ? false : pieces[0].far;
    }
    const at = (s) => {
      for (let i = 1; i < pts.length; i++)
        if (s <= cum[i] + 1e-9) {
          const k = (s - cum[i - 1]) / (cum[i] - cum[i - 1] || 1);
          return [
            pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * k,
            pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * k,
          ];
        }
      return pts[pts.length - 1];
    };
    const sub = (a, b) => {
      const r = [at(a)];
      for (let i = 1; i < pts.length - 1; i++)
        if (cum[i] > a && cum[i] < b) r.push(pts[i]);
      r.push(at(b));
      return r;
    };
    const m = moved[ci];
    for (const pc of pieces) {
      if (pc.s1 - pc.s0 < U(6)) continue;
      const pp = sub(pc.s0, pc.s1);
      const inside = (sg) => {
        const [sm] = sOf([
          (sg[0][0] + sg[1][0]) / 2,
          (sg[0][1] + sg[1][1]) / 2,
        ]);
        return sm >= pc.s0 && sm < pc.s1;
      };
      const wi = m.windowSegs
        .map((sg, k) => (inside(sg) ? k : -1))
        .filter((k) => k >= 0);
      const di = m.doorSegs
        .map((sg, k) => (inside(sg) ? k : -1))
        .filter((k) => k >= 0);
      // the run under it (thickness typing, colour): a parallel run inside the band
      const b0 = inBand(at((pc.s0 + pc.s1) / 2), U(6));
      let run = -1,
        rl = 0;
      if (b0)
        runs.forEach((R, ri) => {
          if (!R || Math.abs(R.ux * b0.ux + R.uy * b0.uy) < 0.995) return;
          const sg = R.nx * b0.nx + R.ny * b0.ny > 0 ? 1 : -1;
          if (Math.abs(sg * R.c - b0.off) > b0.th / 2 + U(2)) return;
          const len = (R.t1 || 0) - (R.t0 || 0);
          if (len > rl) {
            rl = len;
            run = ri;
          }
        });
      const lf = (pc.s1 - pc.s0) * ftPerUnit;
      out.push({
        tag: pc.tag,
        wet: false,
        pts: simplify(pp),
        lf,
        run,
        runLf: run >= 0 ? { [run]: lf } : {},
        th: c.th,
        windows: wi.map((k) => m.windows[k]),
        windowSegs: wi.map((k) => m.windowSegs[k]),
        doors: di.map((k) => m.doors[k]),
        doorSegs: di.map((k) => m.doorSegs[k]),
        doorKinds: di.map((k) => m.doorKinds[k]),
        doorNoSwing: di.map((k) => m.doorNoSwing[k]),
        exterior: true,
        ...(pc.tag
          ? {
              tagEv: !marks.length
                ? {
                    src: "check",
                    reasons: [
                      "exterior wall — no exterior tag found outside it; typed from the lines it replaced",
                    ],
                    alt: null,
                  }
                : pc.far
                  ? {
                      src: "check",
                      reasons: [
                        `exterior wall — no tag on this stretch; ${pc.tag} carried from along the wall`,
                      ],
                      alt: null,
                    }
                  : { src: "tag", reasons: [], alt: null },
            }
          : {}),
      });
    }
  });
  return out;
}
